import type { HelperAssignedItem } from '../services/helper/helperAssignedItemsService'

export type FocusModeTimerConfig = { plannedMs: number }
export type FocusModeTaskFeedSnapshot = { items: HelperAssignedItem[]; openTodos: () => void }

export type CompletedFocusItem = {
  item: HelperAssignedItem
  elapsedMs: number
}

export type FocusModeExitSummary = {
  elapsedMs: number
  totalElapsedMs: number
  totalTasksWorked: number
  completedCount: number
}

export type FocusModeState = {
  active: boolean
  workItem: HelperAssignedItem | null
  queue: HelperAssignedItem[]
  completedItems: CompletedFocusItem[]
  sessionStartMs: number | null
  timerConfig: FocusModeTimerConfig
  paused: boolean
  elapsedBeforePauseMs: number
}

export type FocusModeContextValue = FocusModeState & {
  pendingEnter: boolean
  enter: (workItem?: HelperAssignedItem | HelperAssignedItem[]) => void
  exit: () => FocusModeExitSummary
  nextTask: () => void
  addToQueue: (items: HelperAssignedItem | HelperAssignedItem[]) => void
  removeFromQueue: (itemId: string) => void
  pause: () => void
  resume: () => void
  addMinutes: (m: number) => void
  restart: () => void
  cancelPendingEnter: () => void
  setPlannedMinutes: (minutes: number) => void
  registerTaskFeed: (snapshot: FocusModeTaskFeedSnapshot) => void
  taskFeedItems: HelperAssignedItem[]
  openTodos: () => void
}

export const DEFAULT_TIMER_CONFIG: FocusModeTimerConfig = { plannedMs: 25 * 60 * 1000 }

export const DEFAULT_FOCUS_MODE_STATE: FocusModeState = {
  active: false,
  workItem: null,
  queue: [],
  completedItems: [],
  sessionStartMs: null,
  timerConfig: DEFAULT_TIMER_CONFIG,
  paused: false,
  elapsedBeforePauseMs: 0,
}

export const elapsedMsFor = (
  state: { active: boolean; paused: boolean; sessionStartMs: number | null; elapsedBeforePauseMs: number },
  now: number,
) =>
  state.elapsedBeforePauseMs + (state.active && !state.paused && state.sessionStartMs !== null
    ? Math.max(0, now - state.sessionStartMs)
    : 0)

export const computeExitSummary = (
  state: {
    active: boolean
    paused: boolean
    sessionStartMs: number | null
    elapsedBeforePauseMs: number
    workItem: HelperAssignedItem | null
    completedItems: CompletedFocusItem[]
  },
  now: number,
): FocusModeExitSummary & { workItemTitle?: string } => {
  const currentElapsedMs = elapsedMsFor(state, now)
  const completedCount = state.completedItems.length
  const totalElapsedMs = state.completedItems.reduce((sum, item) => sum + item.elapsedMs, 0) + currentElapsedMs
  const totalTasksWorked = completedCount + (state.workItem ? 1 : 0)
  return {
    elapsedMs: currentElapsedMs,
    totalElapsedMs,
    totalTasksWorked,
    completedCount,
    workItemTitle: state.workItem?.title,
  }
}

export const formatMinutesSeconds = (milliseconds: number) => {
  const seconds = Math.floor(Math.max(0, milliseconds) / 1000)
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}


